import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Rating from "@mui/material/Rating";
import Tooltip from "@mui/material/Tooltip";
import SuiBadge from "components/SuiBadge";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";

import LocationOnIcon from "@mui/icons-material/LocationOn";

import { capitalCase } from "capital-case";

import { MANUFACTURER_SERVICES } from "utils/data";

function ManufacturerCard({
  isSelected,
  handleSelectUser,
  image,
  address,
  title,
  description,
  story,
  action,
  authors = [],
  rating = 0,
  services = [],
  selectable = false,
  account_status = "",
}) {
  const renderServiceBadge = MANUFACTURER_SERVICES.filter((s) => services.includes(s.value)).map(
    (s, index) => {
      return (
        <div>
          <SuiBox key={index} mr={index === s.length - 1 ? 0 : 0.5} mb={0.5}>
            <SuiBadge
              textTransform="none"
              color={s.color}
              size="xs"
              badgeContent={capitalCase(s.label)}
              container
            />
          </SuiBox>
        </div>
      );
    }
  );

  return (
    <Card
      sx={{
        maxWidth: 360,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "visible",
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        borderRadius: "xl",
      }}
    >
      <SuiBox>
        {selectable ? (
          <SuiBox
            display="flex"
            alignItems="center"
            position="relative"
            height={180}
            border="none"
            borderRadius="xl"
            sx={{
              backgroundImage: image
                ? `url(${image})`
                : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                    `${linearGradient(
                      rgba(gradients.secondary.main, 0.2),
                      rgba(gradients.secondary.state, 0.4)
                    )}`,
              backgroundSize: "cover",
              backgroundPosition: "50%",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => handleSelectUser(!isSelected)}
          />
        ) : (
          <Link to={action.route}>
            <SuiBox
              display="flex"
              alignItems="center"
              position="relative"
              height={180}
              border="none"
              borderRadius="xl"
              sx={{
                backgroundImage: image
                  ? `url(${image})`
                  : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                      `${linearGradient(
                        rgba(gradients.secondary.main, 0.2),
                        rgba(gradients.secondary.state, 0.4)
                      )}`,
                backgroundSize: "cover",
                backgroundPosition: "50%",
                overflow: "hidden",
                cursor: "pointer",
              }}
            />
          </Link>
        )}
      </SuiBox>
      <SuiBox py={1} px={1.5}>
        <SuiBox sx={{ minHeight: "160px" }}>
          <SuiBox mb={1}>
            <SuiBox display="flex" alignItems="flex-start" justifyContent="space-between">
              <Tooltip title={title}>
                {selectable ? (
                  <SuiTypography
                    variant="h6"
                    fontWeight="medium"
                    textTransform="capitalize"
                    lineHeight="1.2"
                    sx={{
                      maxWidth: 360,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSelectUser(!isSelected)}
                  >
                    {title}
                  </SuiTypography>
                ) : (
                  <SuiTypography
                    variant="h6"
                    fontWeight="medium"
                    textTransform="capitalize"
                    lineHeight="1.2"
                    sx={{
                      maxWidth: 360,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                      cursor: "pointer",
                    }}
                    component={Link}
                    to={action.route}
                  >
                    {title}
                  </SuiTypography>
                )}
              </Tooltip>
              {selectable ? (
                <Checkbox
                  onChange={handleSelectUser}
                  checked={isSelected}
                  color="primary"
                  sx={{ mt: 0.5, ml: 1, color: "red" }}
                />
              ) : null}
            </SuiBox>
            {address ? (
              <SuiBox display="flex" my={0.5} justifyContent="space-between" alignItems="center">
                <SuiTypography
                  variant="body2"
                  fontWeight="regular"
                  color="secondary"
                  display="flex"
                  alignItems="center"
                >
                  <LocationOnIcon />
                  {address}
                </SuiTypography>
                {account_status == "active" ? (
                  <SuiBadge
                    variant="contained"
                    size="xs"
                    color="success"
                    badgeContent={"Claimed"}
                  />
                ) : null}
              </SuiBox>
            ) : (
              <br />
            )}
          </SuiBox>
          <SuiBox mb={1} lineHeight={0}>
            {description ? (
              <Tooltip title={description}>
                <SuiTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                    minHeight: "32px",
                  }}
                >
                  {description}
                </SuiTypography>
              </Tooltip>
            ) : (
              <SuiTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  minHeight: "32px",
                }}
              >
                {story ? story.slice(0, 500) : "There is no description for this manufacturer!"}
              </SuiTypography>
            )}
            <SuiBox display="flex" alignItems="center" sx={{ flexWrap: "wrap", mt: 1 }}>
              {renderServiceBadge}
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <SuiButton
            component={Link}
            to={action.route}
            variant="outlined"
            size="small"
            color={action.color}
            sx={{
              fontSize: "12px",
              lineHeight: 1,
              py: 0,
              px: 1,
            }}
          >
            {action.label}
          </SuiButton>
          <Tooltip
            title={
              <SuiTypography
                sx={{ ml: 0.5, fontSize: 14 }}
                variant="text"
                fontWeight="regular"
                color="secondary"
              >
                {rating >= 0 ? (rating / 20).toFixed(1) : 0} of 5 stars
              </SuiTypography>
            }
          >
            <SuiBox display="flex" alignItems="center">
              <Rating
                name="half-rating-read"
                value={rating >= 0 ? (rating / 20).toFixed(1) : 0}
                precision={0.1}
                size="small"
                readOnly
                sx={{ fontSize: 14 }}
              />

              {/** 
            <SuiTypography
              sx={{ ml: 0.5, fontSize: 14 }}
              variant="text"
              fontWeight="regular"
              color="secondary"
            >
              {rating >= 0 ? (rating / 20).toFixed(1) : 0}
            </SuiTypography>*/}
            </SuiBox>
          </Tooltip>
        </SuiBox>
      </SuiBox>
    </Card>
  );
}

// Setting default values for the props of ProjectCard
ManufacturerCard.defaultProps = {
  authors: [],
};

// Typechecking props for the ProjectCard
ManufacturerCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal"]),
    route: PropTypes.string.isRequired,
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
      "white",
    ]).isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  authors: PropTypes.arrayOf(PropTypes.object),
};

export default ManufacturerCard;
